import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  Alert,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const AddEmployeeChoiceDialog = ({
  open,
  onClose,
  onSelectAddEmployee,
  onSelectImportExcel,
  hasHrmsIntegration = false,
}) => {
  const theme = useTheme();

  const handleClose = () => {
    onClose();
  };

  const handleSelectAddEmployee = () => {
    if (!hasHrmsIntegration) {
      onSelectAddEmployee();
      handleClose();
    }
  };

  const handleSelectImportExcel = () => {
    if (!hasHrmsIntegration) {
      onSelectImportExcel();
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography variant="h5" fontWeight={600} color="#111827">
          Add Employee
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#6b7280" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ background: theme.customColors.background.main }}
      >
        <Alert
          severity="info"
          sx={{
            mb: 3,
            backgroundColor: theme.customColors.background.info,
            border: `1px solid ${theme.customColors.border.accent}`,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ fontSize: "14px", color: theme.customColors.text.strong }}
          >
            Choose how you would like to add a new employee.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                cursor: hasHrmsIntegration ? "not-allowed" : "pointer",
                border: `1px solid ${theme.customColors.border.main}`,
                borderRadius: "12px",
                background: theme.customColors.background.main,
                boxShadow: theme.customColors.shadow.main,
                transition: "all 0.2s ease",
                height: "100%",
                opacity: hasHrmsIntegration ? 0.6 : 1,
                "&:hover": {
                  borderColor: hasHrmsIntegration
                    ? theme.customColors.border.main
                    : theme.palette._blue.main,
                  backgroundColor: theme.customColors.background.hover,
                  transform: hasHrmsIntegration ? "none" : "translateY(-2px)",
                  boxShadow: hasHrmsIntegration
                    ? "none"
                    : "0 4px 12px rgba(0,0,0,0.1)",
                },
              }}
              onClick={handleSelectAddEmployee}
            >
              <CardContent
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  pb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: theme.palette._blue.light,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AddIcon
                      sx={{
                        fontSize: 20,
                        color: theme.palette._blue.main,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{
                        fontSize: "15px",
                        color: theme.customColors.text.strong,
                        mb: 0.5,
                      }}
                    >
                      Add Employee
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "12px", lineHeight: 1.3 }}
                    >
                      Add a single employee manually with their details and information.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                cursor: hasHrmsIntegration ? "not-allowed" : "pointer",
                border: `1px solid ${theme.customColors.border.main}`,
                borderRadius: "12px",
                background: theme.customColors.background.main,
                boxShadow: theme.customColors.shadow.main,
                transition: "all 0.2s ease",
                height: "100%",
                opacity: hasHrmsIntegration ? 0.6 : 1,
                "&:hover": {
                  borderColor: hasHrmsIntegration
                    ? theme.customColors.border.main
                    : theme.palette.warning.main,
                  backgroundColor: theme.customColors.background.hover,
                  transform: hasHrmsIntegration ? "none" : "translateY(-2px)",
                  boxShadow: hasHrmsIntegration
                    ? "none"
                    : "0 4px 12px rgba(0,0,0,0.1)",
                },
              }}
              onClick={handleSelectImportExcel}
            >
              <CardContent
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  pb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: theme.customColors.status.warning.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <UploadFileIcon
                      sx={{
                        fontSize: 20,
                        color: theme.customColors.status.warning.main,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{
                        fontSize: "15px",
                        color: theme.customColors.text.strong,
                        mb: 0.5,
                      }}
                    >
                      Import Excel
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "12px", lineHeight: 1.3 }}
                    >
                      Import multiple employees at once from an Excel file.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
          px: 3,
          pb: 2.5,
          pt: 2,
        }}
      >
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            backgroundColor: theme.customColors.button.hover,
            "&:hover": { backgroundColor: theme.customColors.button.primary },
            fontSize: "14px",
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEmployeeChoiceDialog;

